import React, { ComponentProps, CSSProperties, useState } from "react";
import { css } from "@emotion/react";
import {
  color,
  convertHexToRGBA,
  TextField,
  FieldLabel,
} from "@moneyforward/mfx_biz_react_components";
import { FormErrorMessage } from "@/components/molecules/forms/FormErrorMessage";
import { useIsSpMode } from "@/hooks";
import { PasswordMeter } from "@/components/atoms";
import { get, RegisterOptions, useFormContext } from "react-hook-form";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";

type Props = ComponentProps<typeof TextField> & {
  name: string;
  requiredLabel?: boolean;
  registerOptions?: RegisterOptions;
  textAlign?: CSSProperties["textAlign"];
  usePasswordMeter?: boolean;
  enableAutofill?: boolean;
  isTemporary?: boolean;
  labelText?: string;
};

const passwordFormField = (
  props: Props,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const isSpMode = useIsSpMode();

  const { register, formState } = useFormContext();
  const { errors } = formState;
  const { textAlign, registerOptions, ...inputProps } = props;
  const methods = register(props.name, registerOptions);
  const isError = get(errors, props.name);
  const autoComplete = props.enableAutofill
    ? "current-password"
    : "new-password";

  const state = isError ? "error" : "default";
  const fieldLabelText = props.isTemporary ? "仮パスワード" : "パスワード";

  const [inputType, setInputType] = useState("password");
  const [eyeDisplay, setEyeDisplay] = useState("");
  const [eyeOffDisplay, setEyeOffDisplay] = useState("none");
  const [password, setPassword] = useState("");

  const clickIcon = () => {
    if (inputType === "password") {
      setInputType("text");
      setEyeDisplay("none");
      setEyeOffDisplay("display");
    } else {
      setInputType("password");
      setEyeDisplay("display");
      setEyeOffDisplay("none");
    }
  };

  return (
    <div css={!isSpMode ? container : {}}>
      <div css={labelWrapper}>
        {props.requiredLabel ? (
          <FieldLabel isRequired>{fieldLabelText}</FieldLabel>
        ) : (
          <FieldLabel>{fieldLabelText}</FieldLabel>
        )}
      </div>
      <div css={inputWrapper}>
        <div css={box}>
          <input
            css={[
              styles[state],
              textAlign && {
                textAlign,
              },
            ]}
            type={inputType}
            placeholder={""}
            {...inputProps}
            name={methods.name}
            ref={(el) => {
              methods.ref(el);
              if (typeof ref === "function") {
                ref(el);
                return;
              }
              if (ref) {
                ref.current = el;
              }
            }}
            onChange={(e) => {
              setPassword(e.target.value);
              methods.onChange(e);
              props.onChange && props.onChange(e);
            }}
            onBlur={(e) => {
              methods.onBlur(e);
              props.onBlur && props.onBlur(e);
            }}
            autoComplete={autoComplete}
          />
          <span css={iconWrapper} onClick={clickIcon}>
            <IoMdEye size="1.5em" color="#666" display={eyeDisplay} />
            <IoMdEyeOff size="1.5em" color="#666" display={eyeOffDisplay} />
          </span>
        </div>
        <div css={errorWrapper}>
          <FormErrorMessage name={props.name} />
        </div>
        {props.usePasswordMeter && (
          <div>
            <PasswordMeter password={password} labelText={props.labelText} />
          </div>
        )}
      </div>
    </div>
  );
};

export const PasswordFormField = React.forwardRef(passwordFormField);

const container = css({
  display: "flex",
});

const labelWrapper = css({
  width: "160px",
  marginRight: "16px",
  paddingTop: "10px",
  paddingBottom: "4px",
});

const inputWrapper = css({
  width: "100%",
  flex: 1,
});

const errorWrapper = css({
  flex: 1,
});

const box = css({
  position: "relative",
});

const iconWrapper = css({
  position: "absolute",
  top: "8px",
  right: "20px",
});

const base = css({
  boxSizing: "border-box",
  display: "block",
  width: "100%",
  lineHeight: "40px",
  height: 40,
  paddingLeft: 16,
  paddingRight: 16,
  borderRadius: 4,
  borderColor: color.gray300,
  borderStyle: "solid",
  borderWidth: 1,
  fontSize: "inherit",
  fontFamily: "inherit",

  "&:hover": {
    borderColor: color.black,
  },

  "&:focus": {
    outline: "none",
    borderColor: color.primary,
  },

  "&:disabled": {
    color: convertHexToRGBA(color.black, 0.3),
    borderColor: color.gray300,
  },

  "&::placeholder": {
    color: color.gray600,
    opacity: 1,
  },
});

const styles = {
  default: base,
  error: css(base, {
    borderColor: color.attention,
  }),
};
